import { debounce } from 'radash'
import { useEffect } from 'react'

import { GENERATOR_SEARCH_PARAM } from '@/generator/shared'
import { getStore } from '@/store'

import { contentFormValidityAtom } from '../../generatorState'
import { QR_TYPE } from '../../generatorTypes'
import { useGeneratorSearchParam } from '../../hooks'
import { getGeneratorSearchParam } from '../../utils/generatorSearchParam'
import { QR_TYPE_TO_CONTENT_ATOM } from '../../utils/qrTypeToContentAtom'
import { isContentFormSubmittingAtom, updateQr } from '../contentFormUtils'

export const useUpdateQrOnContentChange = (qrType: QR_TYPE) => {
	const qrId = useGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)

	// Handle updates when QR already exists and form is valid
	useEffect(() => {
		if (!qrId) {
			return
		}

		const contentAtom = QR_TYPE_TO_CONTENT_ATOM[qrType]
		let unsubscribeContent: (() => void) | null = null

		// Create debounced function inside effect so each effect instance has its own
		const debouncedUpdateQr = debounce(
			{ delay: 1_000 },
			async (params: Parameters<typeof updateQr>[0]) => {
				try {
					await updateQr(params)
				} catch (error) {
					console.error('Failed to update QR:', error)
				}
			},
		)

		const updateQrWithCurrentState = () => {
			const currentSectionsValidity = getStore().get(contentFormValidityAtom)
			const isCurrentlyValid = Object.values(currentSectionsValidity).every(
				(isValid) => isValid,
			)
			const isSubmitting = getStore().get(isContentFormSubmittingAtom)
			const currentQrId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)

			// Only update if form is still valid, not submitting, and QR still exists
			if (isCurrentlyValid && !isSubmitting && currentQrId) {
				debouncedUpdateQr({
					id: currentQrId,
					qrType,
				})
			}
		}

		updateQrWithCurrentState()

		const subscribeToContentChanges = () => {
			if (unsubscribeContent) {
				unsubscribeContent()
			}

			unsubscribeContent = getStore().sub(contentAtom, updateQrWithCurrentState)
		}

		// Subscribe to validity changes to start/stop content subscription
		const unsubscribeValidity = getStore().sub(contentFormValidityAtom, () => {
			const sectionsValidity = getStore().get(contentFormValidityAtom)
			const isFormValid = Object.values(sectionsValidity).every(
				(isValid) => isValid,
			)

			// Only subscribe to content changes if form is valid
			if (isFormValid) {
				subscribeToContentChanges()
			} else {
				// Unsubscribe from content changes when form becomes invalid
				if (unsubscribeContent) {
					unsubscribeContent()
					unsubscribeContent = null
				}
			}
		})

		// Trigger initial check
		const sectionsValidity = getStore().get(contentFormValidityAtom)
		const isFormValid = Object.values(sectionsValidity).every(
			(isValid) => isValid,
		)
		if (isFormValid) {
			subscribeToContentChanges()
		}

		return () => {
			unsubscribeValidity()
			if (unsubscribeContent) {
				unsubscribeContent()
			}
			// Cancel any pending debounced calls when effect cleans up
			if (typeof debouncedUpdateQr.cancel === 'function') {
				debouncedUpdateQr.cancel()
			}
		}
	}, [qrId, qrType])
}
